import React, { useState, useEffect } from 'react';
import { AdminService } from '../../services/APIService';
import { extractArrayFromResponse, entityFilters } from '../../utils/apiHelpers';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    status: 'active',
    password: '',
    phone: ''
  });

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AdminService.getAllUsers();
      
      // Process the response using our utility function
      const usersArray = extractArrayFromResponse(response, 'users', 'users', entityFilters.users);
      
      if (usersArray.length > 0) {
        setUsers(usersArray);
      } else {
        console.error('No valid users data found in API response');
        setError('No user data available. The server may be experiencing issues.');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
      setUsers([]); // Set to empty array to avoid .map errors
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the edit modal
  const handleEditUser = (user) => {
    // Prevent editing admin@gmail.com user through UI
    if (user.email === 'admin@gmail.com') {
      setError('The main administrator account cannot be edited through this interface.');
      return;
    }
    
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active',
      password: '', // Empty by default, as we don't store or retrieve passwords
      phone: user.phone || ''
    });
    setModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent editing admin@gmail.com user's role
    if (selectedUser?.email === 'admin@gmail.com' && (name === 'role' || name === 'status')) {
      setError('Cannot modify the main administrator account role or status.');
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Update user role
  const handleRoleChange = async (userId, role) => {
    // Find the user by ID
    const user = users.find(u => u.id === userId || u._id === userId);
    
    // Validate that we found the user
    if (!user) {
      setError('Cannot update role: User not found');
      return;
    }
    
    // Prevent editing admin@gmail.com user
    if (user.email === 'admin@gmail.com') {
      setError('Cannot modify the main administrator account.');
      return;
    }

    // Use _id if available, otherwise fall back to id
    const validUserId = user._id || user.id;
    
    try {
      await AdminService.updateUserRole(validUserId, role);
      // Update user in local state
      setUsers(users.map(u => 
        (u.id === userId || u._id === userId) ? { ...u, role } : u
      ));
      // Clear any previous errors on success
      setError(null);
    } catch (error) {
      console.error('Error updating role:', error);
      const errorMessage = error.response 
        ? `Failed to update user role (${error.response.status}): ${error.response.data?.message || error.response.statusText}` 
        : 'Failed to update user role. Network error or server unavailable.';
      setError(errorMessage);
    }
  };

  // Update user status
  const handleStatusChange = async (userId, status) => {
    // Find the user by ID
    const user = users.find(u => u.id === userId || u._id === userId);
    
    // Validate that we found the user
    if (!user) {
      setError('Cannot update status: User not found');
      return;
    }
    
    // Prevent editing admin@gmail.com user
    if (user.email === 'admin@gmail.com') {
      setError('Cannot modify the main administrator account.');
      return;
    }
    
    // Use _id if available, otherwise fall back to id
    const validUserId = user._id || user.id;
    
    try {
      await AdminService.updateUserStatus(validUserId, status);
      // Update user in local state
      setUsers(users.map(u => 
        (u.id === userId || u._id === userId) ? { ...u, status } : u
      ));
      // Clear any previous errors on success
      setError(null);
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMessage = error.response 
        ? `Failed to update user status (${error.response.status}): ${error.response.data?.message || error.response.statusText}` 
        : 'Failed to update user status. Network error or server unavailable.';
      setError(errorMessage);
    }
  };

  // Handle save user data
  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    // Prevent editing admin@gmail.com user's critical fields
    if (selectedUser.email === 'admin@gmail.com') {
      // Allow only password updates for admin@gmail.com
      if (formData.role !== selectedUser.role || formData.status !== selectedUser.status) {
        setError('Cannot modify the main administrator account role or status.');
        return;
      }
    }

    // Create a clean copy of form data
    const updatedData = { ...formData };
    
    // Remove empty password field if not being changed
    if (!updatedData.password.trim()) {
      delete updatedData.password;
    }

    // Use _id if available, otherwise fall back to id
    const validUserId = selectedUser._id || selectedUser.id;
    
    // Validate that we have a valid ID
    if (!validUserId) {
      setError('Cannot update user: No valid user ID found');
      return;
    }

    try {
      await AdminService.updateUser(validUserId, updatedData);
      // Update the user in the local state, but don't include password in local state
      const userForState = { ...updatedData };
      delete userForState.password; // Don't store password in state
      
      setUsers(users.map(user => 
        (user._id === validUserId || user.id === validUserId) ? { ...user, ...userForState } : user
      ));
      setModalOpen(false);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response 
        ? `Failed to update user (${error.response.status}): ${error.response.data?.message || error.response.statusText}` 
        : 'Failed to update user. Network error or server unavailable.';
      setError(errorMessage);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    // Find the user by ID
    const user = users.find(u => u.id === userId || u._id === userId);
    
    // Validate that we found the user
    if (!user) {
      setError('Cannot delete: User not found');
      return;
    }
    
    // Prevent deleting admin@gmail.com user
    if (user.email === 'admin@gmail.com') {
      setError('Cannot delete the main administrator account.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    // Use _id if available, otherwise fall back to id
    const validUserId = user._id || user.id;

    try {
      await AdminService.deleteUser(validUserId);
      // Remove user from local state
      setUsers(users.filter(u => (u._id !== validUserId && u.id !== validUserId)));
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response 
        ? `Failed to delete user (${error.response.status}): ${error.response.data?.message || error.response.statusText}` 
        : 'Failed to delete user. Network error or server unavailable.';
      setError(errorMessage);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage all users of the Karigar Foundation platform. You can edit user details, change roles, update status, and reset passwords.
          <span className="text-amber-600 font-medium ml-1">Note: The main admin account (admin@gmail.com) cannot be modified.</span>
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : users && Array.isArray(users) && users.length > 0 ? (
        /* Users table */
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id || user._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                            {user.name ? user.name.charAt(0) : '?'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">
                            Registered: {new Date(user.created_at || user.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.phone || <span className="text-gray-400">—</span>}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="border border-gray-300 rounded-md text-sm py-1 px-2"
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id || user._id, e.target.value)}
                        disabled={user.email === 'admin@gmail.com'}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="border border-gray-300 rounded-md text-sm py-1 px-2"
                        value={user.status}
                        onChange={(e) => handleStatusChange(user.id || user._id, e.target.value)}
                        disabled={user.email === 'admin@gmail.com'}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="blocked">Blocked</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleEditUser(user)}
                        className={`${user.email === 'admin@gmail.com' ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-900'} mr-4`}
                        disabled={user.email === 'admin@gmail.com'}
                        title={user.email === 'admin@gmail.com' ? 'Cannot edit main admin account' : 'Edit user'}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id || user._id)}
                        className={`${user.email === 'admin@gmail.com' ? 'text-gray-400' : 'text-red-600 hover:text-red-900'}`}
                        disabled={user.email === 'admin@gmail.com'}
                        title={user.email === 'admin@gmail.com' ? 'Cannot delete main admin account' : 'Delete user'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* No users message */
        <div className="bg-white shadow-md rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {error ? 'There was an error loading the users.' : 'No users have been added yet.'}
          </p>
        </div>
      )}

      {/* Edit User Modal */}
      {modalOpen && selectedUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSaveUser}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      name="role"
                      id="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      disabled={selectedUser?.email === 'admin@gmail.com'}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      id="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      id="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Leave blank to keep current password"
                    />
                    <p className="mt-1 text-xs text-gray-500">Leave blank if you don't want to change the password.</p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
